export const installCmd = `npm install @taar/webrtc-client`;

export const reactProviderExample = `import { TaarWebRTCProvider, TaarWebRTCClientUI } from '@taar/webrtc-client';

function App() {
  // This function will be called when TaarWebRTC is ready to register
  const handleReady = async (nonce: string) => {
    // Make a POST request to your backend to register the agent
    const response = await fetch('https://<your-server>/api/register-agent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ nonce })
    });
    const { creds } = await response.json();
    return creds;
  };

  return (
    <TaarWebRTCProvider
      debug={true} // Enable debug logs
      onReady={handleReady}
      onCallStarted={(callInfo) => console.log('Call started:', callInfo)}
      onCallEnded={() => console.log('Call ended')}
      onError={(error) => console.error('Error:', error)}
      onMute={() => console.log('Microphone muted')}
      onUnmute={() => console.log('Microphone unmuted')}
      onVolumeChange={(volume) => console.log('Volume changed:', volume)}
      onDisconnect={() => console.log('Disconnected')}
    >
      {/* Use the default UI component */}
      <TaarWebRTCClientUI agentCredentials="your-agent-credentials" />
    </TaarWebRTCProvider>
  );
}
`;

export const directUsageExample = `import { TaarWebRTC } from '@taar/webrtc-client';

// Create a new instance
const client = new TaarWebRTC({
  debug: true,
  onCallStarted: (callInfo) => console.log('Call started:', callInfo),
  onCallEnded: () => console.log('Call ended'),
  onError: (error) => console.error('Error:', error),
  onReady: async (nonce) => {
    // Register your agent with the backend
    const response = await fetch('https://your-server/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ nonce })
    });
    const { creds } = await response.json();
    return creds;
  }
});

// Create an audio element for the call
const audioElement = document.createElement('audio');
audioElement.autoplay = true;
document.body.appendChild(audioElement);

// Register the client
await client.register('your-agent-credentials', audioElement);

// Make a call
await client.makeCall('destination@taar.ai');

// End the call
await client.endCall();

// Cleanup when done
await client.unregister();`;

export const checkNetworkCmd = `# Test API connectivity
curl https://api.taar.ai/health
# Test WebRTC server connectivity
curl https://clients.taar.ai/health`;

export const verifyAudioElement = `// Verify audio element is properly created
const audioElement = document.querySelector('audio');
console.log('Audio element:', audioElement);
console.log('Audio context state:', audioElement?.context?.state);`;

export const websocketTest = `const ws = new WebSocket('wss://clients.taar.ai');
ws.onopen = () => console.log('WebSocket connected');
ws.onerror = (error) => console.error('WebSocket error:', error);`;

export const stunTest = `const pc = new RTCPeerConnection();
pc.createDataChannel('test');
pc.createOffer()
  .then(offer => pc.setLocalDescription(offer))
  .catch(console.error);`;

export const backendRegisterAgent = `// Your backend API endpoint (e.g., /api/register-agent)
async function registerAgent(req: Request, res: Response) {
  try {
    // 1. Get the nonce from the request body
    const { nonce } = req.body;
    
    // 2. Make the registration request to Your Backend
    const response = await fetch('https://your-backend/agent/register', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Add your API key or authentication headers here
        'Authorization': \`Bearer \${process.env.TAAR_API_KEY}\`
      },
      body: JSON.stringify({ nonce })
    });

    // 3. Get the agent credentials from response headers
    // ⚠️ IMPORTANT: Never expose these in the response body
    const agentId = response.headers.get('x-agent-id');
    const agentCredentials = response.headers.get('x-agent-credentials');

    // 4. Store these securely in your backend
    // You might want to associate them with your user/session
    await storeAgentCredentials(req.user.id, {
      agentId,
      agentCredentials
    });

    // 5. Return only a success status
    res.status(200).json({ success: true });
  } catch (error) {
    console.error('Registration failed:', error);
    res.status(500).json({ error: 'Registration failed' });
  }
}`;

export const frontendIntegration = `// In your React component
function TaarWebRTCIntegration() {
  const handleReady = async (nonce: string) => {
    try {
      // 1. Call your backend endpoint with the nonce
      const response = await fetch('/api/register-agent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Add your authentication headers
          'Authorization': \`Bearer \${userToken}\`
        },
        body: JSON.stringify({ nonce })
      });

      if (!response.ok) {
        throw new Error('Registration failed');
      }

      // 2. Get the agent credentials from your backend
      // Your backend should have stored these securely
      const { agentCredentials } = await response.json();

      // 3. Initialize TaarWebRTC with the credentials
      return agentCredentials;
    } catch (error) {
      console.error('Failed to register agent:', error);
      throw error;
    }
  };

  return (
    <TaarWebRTCProvider
      onReady={handleReady}
      // ... other props
    >
      <TaarWebRTCClientUI />
    </TaarWebRTCProvider>
  );
}`;

export const authFlowMermaid = `sequenceDiagram
    Client->>Your Backend: 1. Request registration (with auth)
    Your Backend->>Taar.ai: 2. Register with nonce
    Taar.ai-->>Your Backend: 3. Return credentials in headers
    Your Backend->>Database: 4. Store credentials securely
    Your Backend-->>Client: 5. Return success only`;

export const credentialStorage = `// Example secure storage structure
interface AgentCredentials {
  agentId: string;
  agentCredentials: string;
  userId: string;        // Your user's ID
  createdAt: Date;
  expiresAt: Date;       // If credentials expire
  lastUsed: Date;        // For tracking usage
}

// Store in your database
await db.agents.create({
  data: {
    agentId,
    agentCredentials: encryptedCredentials, // Encrypt before storing
    userId: user.id,
    createdAt: new Date(),
    expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000), // 24 hours
    lastUsed: new Date()
  }
});`;

export const errorHandling = `// In your backend
async function handleRegistrationError(error: any) {
  // Log the error securely
  await logError({
    type: 'AGENT_REGISTRATION',
    error: error.message,
    timestamp: new Date(),
    // Don't log sensitive data
  });

  // Map Taar.ai errors to user-friendly messages
  const errorMap = {
    'INVALID_NONCE': 'Registration failed: Invalid request',
    'RATE_LIMITED': 'Too many registration attempts',
    'UNAUTHORIZED': 'Invalid API credentials',
    // ... other error mappings
  };

  return {
    status: error.status || 500,
    message: errorMap[error.code] || 'Registration failed'
  };
}`;

export const registerWithRetry = `async function registerWithRetry(nonce: string, retries = 3) {
  for (let i = 0; i < retries; i++) {
    try {
      return await registerAgent(nonce);
    } catch (error) {
      if (error.code === 'RATE_LIMITED') {
        await new Promise(r => setTimeout(r, Math.pow(2, i) * 1000));
        continue;
      }
      throw error;
    }
  }
}`;

export const refreshCredentials = `async function refreshCredentials() {
  // Check if credentials are about to expire
  if (isExpiringSoon(credentials)) {
    // Re-register with new nonce
    const newNonce = await getNewNonce();
    return await registerAgent(newNonce);
  }
  return credentials;
}`; 